import React, {FormEvent, useEffect, useMemo, useRef, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleXmark, faPaperPlane, faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
import {
    FEEDBACK_DIALOG_DESCRIPTION,
    FEEDBACK_DIALOG_TITLE,
    FEEDBACK_RAW_DATA_ACCEPT,
    FEEDBACK_EMAIL_MAX_LENGTH,
    FEEDBACK_MESSAGE_MAX_LENGTH,
    FEEDBACK_RAW_DATA_OPT_IN_HELP,
    FEEDBACK_RAW_DATA_OPT_IN_LABEL,
    FEEDBACK_RAW_DATA_MAX_BYTES,
    FEEDBACK_SENDING_LABEL,
    FEEDBACK_SUBJECT_MAX_LENGTH,
    FEEDBACK_SUBMIT_LABEL,
    FEEDBACK_SUCCESS_MESSAGE,
    FEEDBACK_TYPE_OPTIONS
} from "./constants";
import {getFeedbackAppVersion, getFeedbackBrowserLabel, getFeedbackContextFromPath, getFeedbackDeviceLabel} from "./helpers";
import {submitFeedback} from "../../api/feedback";
import type {FeedbackFileState, FeedbackFormValues, FeedbackModalProps} from "./types";
import './feedback.scss';

const DEFAULT_VALUES: FeedbackFormValues = {
    feedbackType: 'bug',
    subject: '',
    message: '',
    email: '',
    willingToShareRawData: false
};

export default function FeedbackModal({
    isOpen,
    onClose,
    pageRoute,
    pageUrl,
    entityType,
    entityId
}: FeedbackModalProps) {
    const [values, setValues] = useState<FeedbackFormValues>(DEFAULT_VALUES);
    const [rawDataFileState, setRawDataFileState] = useState<FeedbackFileState>({file: null, error: ''});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const dialogRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const derivedContext = useMemo(() => getFeedbackContextFromPath(pageRoute), [pageRoute]);
    const effectiveEntityType = entityType ?? derivedContext.entityType;
    const effectiveEntityId = entityId ?? derivedContext.entityId;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setValues(DEFAULT_VALUES);
        setRawDataFileState({file: null, error: ''});
        setIsSubmitting(false);
        setErrorMessage('');
        setSuccessMessage('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        dialogRef.current?.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.body.classList.add('feedback-modal-open');
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.classList.remove('feedback-modal-open');
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const updateField = (field: keyof FeedbackFormValues, value: string | boolean) => {
        setValues(current => ({...current, [field]: value}));
        setErrorMessage('');
    };

    const updateRawDataFile = (file: File | null) => {
        if (!file) {
            setRawDataFileState({file: null, error: ''});
            return;
        }

        if (file.size > FEEDBACK_RAW_DATA_MAX_BYTES) {
            setRawDataFileState({
                file: null,
                error: 'The selected file is too large. Please choose a file under 40 MB.'
            });
            return;
        }

        setRawDataFileState({file, error: ''});
        setErrorMessage('');
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isSubmitting) {
            return;
        }

        if (!values.message.trim()) {
            setErrorMessage('A message is required.');
            return;
        }

        if (values.willingToShareRawData && !rawDataFileState.file) {
            setErrorMessage('Please attach your original CSV or ZIP file before submitting.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            await submitFeedback({
                feedbackType: values.feedbackType,
                subject: values.subject.trim(),
                message: values.message.trim(),
                email: values.email.trim(),
                pageRoute,
                pageUrl,
                entityType: effectiveEntityType,
                entityId: effectiveEntityId,
                appVersion: getFeedbackAppVersion(),
                browser: getFeedbackBrowserLabel(),
                device: getFeedbackDeviceLabel(),
                willingToShareRawData: values.willingToShareRawData,
                rawDataFile: values.willingToShareRawData ? rawDataFileState.file : null
            });

            setSuccessMessage(FEEDBACK_SUCCESS_MESSAGE);
            setValues(current => ({...current, message: '', subject: '', email: '', willingToShareRawData: false}));
            setRawDataFileState({file: null, error: ''});
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Feedback submission failed.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="feedback-modal" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
                onClose();
            }
        }}>
            <div
                className="feedback-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="feedback-modal-title"
                aria-describedby="feedback-modal-description"
                tabIndex={-1}
                ref={dialogRef}
            >
                <div className="feedback-modal__header">
                    <div>
                        <p className="feedback-modal__kicker">App feedback</p>
                        <h2 className="feedback-modal__title" id="feedback-modal-title">{FEEDBACK_DIALOG_TITLE}</h2>
                        <p className="feedback-modal__description" id="feedback-modal-description">{FEEDBACK_DIALOG_DESCRIPTION}</p>
                    </div>
                    <button type="button" className="feedback-modal__close" onClick={onClose} aria-label="Close feedback form">
                        <FontAwesomeIcon icon={faCircleXmark} aria-hidden="true" />
                    </button>
                </div>

                <form className="feedback-modal__form" onSubmit={handleSubmit}>
                    <div className="feedback-modal__grid">
                        <label className="feedback-modal__field">
                            <span className="feedback-modal__label">What kind of feedback is this?</span>
                            <select
                                className="form-select"
                                value={values.feedbackType}
                                onChange={(event) => updateField('feedbackType', event.target.value)}
                            >
                                {FEEDBACK_TYPE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>

                        <label className="feedback-modal__field">
                            <span className="feedback-modal__label">Subject</span>
                            <input
                                type="text"
                                className="form-control"
                                value={values.subject}
                                maxLength={FEEDBACK_SUBJECT_MAX_LENGTH}
                                onChange={(event) => updateField('subject', event.target.value)}
                                placeholder="Short summary"
                            />
                        </label>
                    </div>

                    <label className="feedback-modal__field">
                        <span className="feedback-modal__label">Message</span>
                        <textarea
                            className="form-control feedback-modal__message"
                            value={values.message}
                            maxLength={FEEDBACK_MESSAGE_MAX_LENGTH}
                            onChange={(event) => updateField('message', event.target.value)}
                            placeholder="Tell me what happened, what you expected, or what you want to see."
                            rows={7}
                            required
                        />
                    </label>

                    <label className="feedback-modal__field">
                        <span className="feedback-modal__label">Email, if you want a reply</span>
                        <input
                            type="email"
                            className="form-control"
                            value={values.email}
                            maxLength={FEEDBACK_EMAIL_MAX_LENGTH}
                            onChange={(event) => updateField('email', event.target.value)}
                            placeholder="name@example.com"
                        />
                    </label>

                    <div className="feedback-modal__raw-data">
                        <label className="form-check feedback-modal__check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={values.willingToShareRawData}
                                onChange={(event) => updateField('willingToShareRawData', event.target.checked)}
                            />
                            <span className="form-check-label">{FEEDBACK_RAW_DATA_OPT_IN_LABEL}</span>
                        </label>
                        <p className="feedback-modal__help">
                            {FEEDBACK_RAW_DATA_OPT_IN_HELP}
                        </p>
                        <label className="feedback-modal__file">
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="form-control"
                                accept={FEEDBACK_RAW_DATA_ACCEPT}
                                onChange={(event) => updateRawDataFile(event.target.files?.[0] ?? null)}
                                disabled={!values.willingToShareRawData}
                            />
                        </label>
                        {rawDataFileState.file ? (
                            <p className="feedback-modal__help">
                                Selected file: {rawDataFileState.file.name} ({(rawDataFileState.file.size / 1024).toFixed(1)} KB)
                            </p>
                        ) : null}
                        {rawDataFileState.error ? (
                            <p className="feedback-modal__help feedback-modal__help--error">{rawDataFileState.error}</p>
                        ) : null}
                    </div>

                    {errorMessage ? (
                        <div className="feedback-modal__alert feedback-modal__alert--error" role="alert">
                            <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
                            <span>{errorMessage}</span>
                        </div>
                    ) : null}

                    {successMessage ? (
                        <div className="feedback-modal__alert feedback-modal__alert--success" role="status">
                            <span>{successMessage}</span>
                        </div>
                    ) : null}

                    <div className="feedback-modal__actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
                            {isSubmitting ? FEEDBACK_SENDING_LABEL : FEEDBACK_SUBMIT_LABEL}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
